import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useSuspendPollingWhileOpen } from "@/lib/dialogPolling";
import { toast } from "sonner";
import { X, Utensils, Droplets, Sparkles } from "lucide-react";
import { petSpeciesMeta, moodEmoji } from "@/lib/vibe";
import { fmtTime } from "@/lib/time";
import PetCareScheduleBlock from "@/components/PetCareScheduleBlock";

const fmtHM = (iso) => fmtTime(iso) || "—";

/**
 * PetCareDialog — non-dog pet care (cat, rabbit, hamster, turtle, bird, fish,
 * reptile, other). Three lightweight actions: Fed, Water change, and a
 * for-fun Mood slider. Dogs also use this dialog but only to log a water
 * change — feed/mood are hidden for dogs.
 */
export default function PetCareDialog({ pet, existing, members, viewerId, onClose, onSaved, onPetUpdated, tier, onOpenUpgrade }) {
  useSuspendPollingWhileOpen();
  const navigate = useNavigate();

  const species = pet.pet_species || "dog";
  const meta = petSpeciesMeta(species);
  const isDog = species === "dog";

  const nonPets = useMemo(
    () => (members || []).filter((m) => m.role !== "pet"),
    [members]
  );
  const [actor, setActor] = useState(
    viewerId && nonPets.some((m) => m.id === viewerId) ? viewerId : nonPets[0]?.id || ""
  );
  const [otherActive, setOtherActive] = useState(false);
  const [otherLabel, setOtherLabel] = useState("");
  const [mood, setMood] = useState(existing?.mood || 5);
  const [busy, setBusy] = useState(null); // "fed" | "water" | "mood" | null

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const resolveBy = () => {
    const by = otherActive ? null : (actor || null);
    const by_label = otherActive ? otherLabel.trim() : "";
    if (!by && !by_label) {
      toast.error("Pick a family member or type a name");
      return null;
    }
    return { by, by_label };
  };

  const doAction = async (action) => {
    if (action !== "mood") {
      const who = resolveBy();
      if (!who) return;
      setBusy(action);
      try {
        const updated = await api.logPetCare({
          pet_id: pet.id,
          action,
          by: who.by,
          by_label: who.by_label,
        });
        onSaved && onSaved(updated);
        toast.success(action === "fed" ? `${pet.name} fed` : `Water changed for ${pet.name}`);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Couldn't save");
      } finally {
        setBusy(null);
      }
      return;
    }
    // mood
    setBusy("mood");
    try {
      const updated = await api.logPetCare({
        pet_id: pet.id,
        action: "mood",
        mood: Number(mood),
      });
      onSaved && onSaved(updated);
      toast.success(`${pet.name}'s mood logged`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save");
    } finally {
      setBusy(null);
    }
  };

  const fedAt = existing?.fed_at;
  const waterAt = existing?.water_change_at;
  const fedBy = existing?.fed_by
    ? members.find((m) => m.id === existing.fed_by)?.name
    : existing?.fed_by_label;
  const waterBy = existing?.water_change_by
    ? members.find((m) => m.id === existing.water_change_by)?.name
    : existing?.water_change_by_label;

  return (
    <div
      className="fixed inset-0 z-50 bg-[color:var(--ink)]/45 backdrop-blur-sm flex items-stretch md:items-center justify-center p-0 md:p-6"
      onClick={onClose}
      data-testid="pet-care-dialog"
    >
      <div
        className="relative bg-[color:var(--bg)] md:rounded-[20px] w-full md:max-w-lg max-h-[100svh] md:max-h-[92svh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-[color:var(--bg)] px-5 md:px-7 pt-5 pb-3 flex items-center justify-between border-b border-[color:var(--line-soft)] z-10">
          <div>
            <div className="label-tiny">
              {meta.emoji} {meta.label} · care
            </div>
            <h2 className="font-display-italic text-3xl md:text-4xl leading-none mt-1 tracking-tight">
              {pet.name}
            </h2>
          </div>
          <div className="flex items-center gap-2">
            {/* Only dogs and cats have a care-history page (other
                species don't get the per-day walks/feeds log). Hide
                the link for those to avoid a dead-end click. */}
            {(species === "dog" || species === "cat") && (
              <button
                type="button"
                onClick={() => {
                  onClose();
                  navigate(`/pets/history/${pet.id}`);
                }}
                className="ff-chip text-[12px] shrink-0 !bg-[#EADFCB] !border-[#D4C4A8] !text-[#5A4A2E] hover:!bg-[#DECDB2]"
                data-testid="pet-care-view-history"
                title={`View ${pet.name}'s care history`}
              >
                History →
              </button>
            )}
            <button
              onClick={onClose}
              className="w-9 h-9 rounded-full hover:bg-[color:var(--bg-soft)] flex items-center justify-center"
              data-testid="pet-care-close"
              aria-label="Close"
            >
              <X className="w-4 h-4" strokeWidth={2} />
            </button>
          </div>
        </div>

        <div className="px-5 md:px-7 py-5 space-y-6">
          {/* Who's logging this */}
          <section>
            <label className="label-tiny block mb-2">Logged by</label>
            <div className="flex flex-wrap gap-1.5" data-testid="pet-care-actor-row">
              {nonPets.map((m) => (
                <button
                  key={m.id}
                  onClick={() => {
                    setActor(m.id);
                    setOtherActive(false);
                  }}
                  className={`ff-chip ${!otherActive && actor === m.id ? "is-active" : ""}`}
                  data-testid={`pet-care-actor-${m.id}`}
                >
                  <span className="text-base">{m.avatar_emoji}</span> {m.name}
                </button>
              ))}
              <button
                onClick={() => setOtherActive(true)}
                className={`ff-chip ${otherActive ? "is-active" : ""}`}
                data-testid="pet-care-actor-other"
              >
                Other
              </button>
            </div>
            {otherActive && (
              <input
                autoFocus
                value={otherLabel}
                onChange={(e) => setOtherLabel(e.target.value)}
                placeholder="e.g. Neighbour, Pet-sitter"
                maxLength={40}
                className="ff-input max-w-[280px] mt-2"
                data-testid="pet-care-other-label"
              />
            )}
          </section>

          {/* Current state */}
          <section className="grid grid-cols-2 gap-3">
            {!isDog && (
              <div className="ff-card p-3">
                <div className="label-tiny">Last fed</div>
                <div className="font-display text-2xl tabular leading-none mt-1 tracking-tighter">
                  {fmtHM(fedAt)}
                </div>
                <div className="text-[12px] text-[color:var(--ink-2)] mt-1 leading-tight">
                  {fedBy ? `by ${fedBy}` : (fedAt ? "" : "not yet today")}
                </div>
              </div>
            )}
            <div className="ff-card p-3">
              <div className="label-tiny">Water change</div>
              <div className="font-display text-2xl tabular leading-none mt-1 tracking-tighter">
                {fmtHM(waterAt)}
              </div>
              <div className="text-[12px] text-[color:var(--ink-2)] mt-1 leading-tight">
                {waterBy ? `by ${waterBy}` : (waterAt ? "" : "not yet today")}
              </div>
            </div>
          </section>

          {/* Actions */}
          <section className="flex flex-wrap gap-2">
            {!isDog && (
              <button
                onClick={() => doAction("fed")}
                disabled={busy !== null}
                className="ff-btn ff-btn-primary inline-flex items-center gap-2"
                data-testid="pet-care-fed-btn"
              >
                <Utensils className="w-4 h-4" strokeWidth={2} />
                {busy === "fed" ? "Saving…" : "Just fed"}
              </button>
            )}
            <button
              onClick={() => doAction("water")}
              disabled={busy !== null}
              className="ff-btn inline-flex items-center gap-2"
              data-testid="pet-care-water-btn"
            >
              <Droplets className="w-4 h-4" strokeWidth={2} />
              {busy === "water" ? "Saving…" : "Fresh water"}
            </button>
          </section>

          {/* Mood — non-dog only, for fun */}
          {!isDog && (
            <section>
              <label className="label-tiny block mb-2">
                How's {pet.name} today? <span className="text-[color:var(--ink-3)]">(just for fun)</span>
              </label>
              <div className="flex items-center gap-3">
                <input
                  type="range"
                  min="1"
                  max="10"
                  value={mood}
                  onChange={(e) => setMood(e.target.value)}
                  className="flex-1"
                  data-testid="pet-care-mood-slider"
                />
                <div
                  className="font-display text-3xl tabular w-14 text-right"
                  data-testid="pet-care-mood-value"
                >
                  {mood}
                </div>
                <span className="text-2xl leading-none">{moodEmoji(Number(mood))}</span>
              </div>
              <div className="flex justify-end mt-3">
                <button
                  onClick={() => doAction("mood")}
                  disabled={busy !== null}
                  className="ff-btn inline-flex items-center gap-2"
                  data-testid="pet-care-mood-save"
                >
                  <Sparkles className="w-4 h-4" strokeWidth={2} />
                  {busy === "mood" ? "Saving…" : "Save mood"}
                </button>
              </div>
            </section>
          )}

          {/* Pet nerd schedule — vet, flea/tick, litter (cat). Hidden for
              non-dog/cat species; locked for pets beyond the tier cap
              (Free: 1, Plus: 2, Grand+: unlimited). */}
          <PetCareScheduleBlock
            pet={pet}
            members={members}
            tier={tier}
            onPetUpdated={onPetUpdated}
            onOpenUpgrade={onOpenUpgrade}
          />
        </div>
      </div>
    </div>
  );
}
